import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { studentsApi, classesApi, studentCodeApi, qrApi, studentFeesApi, schoolYearsApi } from '../lib/api';
import api from '../lib/api';
import ImportWizard from '../components/ImportWizard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, PencilSimple, Trash, MagnifyingGlass, UploadSimple, DownloadSimple, Key, GraduationCap, QrCode, Percent, ArrowsClockwise } from '@phosphor-icons/react';
import AttestationMenu from '../components/students/AttestationMenu';
import StudentFormDialog from '../components/students/StudentFormDialog';
import ParentCodeDialog from '../components/students/ParentCodeDialog';
import StudentDiscountDialog from '../components/students/StudentDiscountDialog';
import BulkServicesDialog from '../components/students/BulkServicesDialog';
import StudentsImportReportDialog from '../components/students/StudentsImportReportDialog';
import { toast } from 'sonner';

const Students = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [parentCode, setParentCode] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('all');
  const [filterCycle, setFilterCycle] = useState('all');
  const [filterEnrollment, setFilterEnrollment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  // iter92 — Selected school year for "toutes classes" exports.
  const [exportYear, setExportYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const fileInputRef = useRef(null);

  // Regularisation: bulk-mark returning students (is_new=False)
  const [regularizeOpen, setRegularizeOpen] = useState(false);
  const [regularizePreview, setRegularizePreview] = useState(null);
  const [regularizing, setRegularizing] = useState(false);
  const canRegularize = isAdmin || user?.role === 'comptable';

  const openRegularize = async () => {
    setRegularizePreview(null);
    setRegularizeOpen(true);
    try {
      const { data } = await studentsApi.regularizeReturningPreview();
      setRegularizePreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du calcul');
      setRegularizeOpen(false);
    }
  };

  const confirmRegularize = async () => {
    setRegularizing(true);
    try {
      const { data } = await studentsApi.regularizeReturning();
      toast.success(`${data.updated} élève(s) marqué(s) comme réinscrits`);
      setRegularizeOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la régularisation');
    } finally { setRegularizing(false); }
  };

  const handleToggleEnrollment = async (isReturning) => {
    if (!selectedStudent) return;
    try {
      await studentsApi.setEnrollmentStatus(selectedStudent.id, !isReturning);
      setSelectedStudent({ ...selectedStudent, is_new: !isReturning });
      toast.success(isReturning ? 'Élève marqué comme Réinscrit' : 'Élève marqué comme Nouveau');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour du statut');
    }
  };

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    father_name: '',
    mother_name: '',
    parent_contact: '',
    email: '',
    nationality: '',
    address: '',
    class_id: '',
    cycle: '',
    date_of_birth: '',
    birth_place: '',
    gender: '',
    photo_base64: ''
  });

  // Discount dialog (admin only)
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountTarget, setDiscountTarget] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    registration_discount_percent: 0,
    reregistration_discount_percent: 0,
    tuition_discount_percent: 0,
    canteen_discount_percent: 0,
    transport_discount_percent: 0,
    discount_reason: '',
  });
  const [discountSaving, setDiscountSaving] = useState(false);

  const openDiscountDialog = async (student) => {
    setDiscountTarget(student);
    setDiscountForm({ registration_discount_percent: 0, reregistration_discount_percent: 0, tuition_discount_percent: 0, canteen_discount_percent: 0, transport_discount_percent: 0, discount_reason: '' });
    setDiscountDialogOpen(true);
    try {
      const { data } = await studentFeesApi.getDiscounts(student.id);
      setDiscountForm({
        registration_discount_percent: data?.registration_discount_percent || 0,
        reregistration_discount_percent: data?.reregistration_discount_percent || 0,
        tuition_discount_percent: data?.tuition_discount_percent || 0,
        canteen_discount_percent: data?.canteen_discount_percent || 0,
        transport_discount_percent: data?.transport_discount_percent || 0,
        discount_reason: data?.discount_reason || '',
      });
    } catch { /* new student, keep zeros */ }
  };

  const saveDiscounts = async () => {
    setDiscountSaving(true);
    try {
      await studentFeesApi.updateDiscounts(discountTarget.id, discountForm);
      toast.success(language === 'en' ? 'Discounts saved' : 'Remises enregistrées');
      setDiscountDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'en' ? 'Error' : 'Erreur'));
    } finally { setDiscountSaving(false); }
  };

  // ===== Bulk activate canteen / transport (admin only) =====
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    class_name: '', has_canteen: false, has_transport: false, transport_zone: '',
    override_existing: false,
  });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [transportZonesList, setTransportZonesList] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  // Students CSV import (iter51) — separate state so we don't collide with the services CSV UI
  const [studentsImportResult, setStudentsImportResult] = useState(null);
  const [studentsImportOpen, setStudentsImportOpen] = useState(false);
  // iter52 — Wizard for xlsx/csv with column mapping
  const [importWizardOpen, setImportWizardOpen] = useState(false);

  const handleDownloadCsvTemplate = async () => {
    setCsvDownloading(true);
    try {
      const { data } = await studentFeesApi.downloadCsvTemplate();
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'student_services_template.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'Modèle téléchargé' : 'Template downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally { setCsvDownloading(false); }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset input so same file can be re-selected
    if (!file) return;
    setCsvUploading(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await studentFeesApi.importServicesCsv(fd);
      setCsvResult(data);
      toast.success(
        language === 'fr'
          ? `${data.updated} élève(s) mis à jour, ${data.error_count} erreur(s)`
          : `${data.updated} student(s) updated, ${data.error_count} error(s)`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur d\'import' : 'Import error'));
    } finally { setCsvUploading(false); }
  };

  const openBulkServicesDialog = async () => {
    setBulkResult(null);
    setCsvResult(null);
    setBulkForm({
      class_name: filterClass !== 'all' ? filterClass : '',
      has_canteen: false, has_transport: false, transport_zone: '',
      override_existing: false,
    });
    try {
      const { data } = await api.get('/fee-config');
      setTransportZonesList(data?.transport_zones || []);
    } catch { setTransportZonesList([]); }
    setBulkDialogOpen(true);
  };

  const submitBulkServices = async () => {
    if (!bulkForm.class_name) {
      toast.error(language === 'fr' ? 'Sélectionnez une classe' : 'Pick a class');
      return;
    }
    if (!bulkForm.has_canteen && !bulkForm.has_transport) {
      toast.error(language === 'fr' ? 'Cochez au moins un service' : 'Tick at least one service');
      return;
    }
    if (bulkForm.has_transport && !bulkForm.transport_zone) {
      toast.error(language === 'fr' ? 'Choisissez une zone de transport' : 'Pick a transport zone');
      return;
    }
    setBulkSaving(true);
    setBulkResult(null);
    try {
      const payload = {
        class_name: bulkForm.class_name,
        override_existing: bulkForm.override_existing,
      };
      if (bulkForm.has_canteen) payload.has_canteen = true;
      if (bulkForm.has_transport) {
        payload.has_transport = true;
        payload.transport_zone = bulkForm.transport_zone;
      }
      const { data } = await studentFeesApi.bulkEnableServices(payload);
      setBulkResult(data);
      toast.success(
        language === 'fr'
          ? `${data.updated} élève(s) mis à jour, ${data.skipped} ignoré(s)`
          : `${data.updated} student(s) updated, ${data.skipped} skipped`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally { setBulkSaving(false); }
  };

  useEffect(() => {
    loadData();
  }, [filterStatus]);

  // iter92 — Fetch school years once for the "toutes classes" year picker.
  useEffect(() => {
    (async () => {
      try {
        const [{ data: all }, { data: active }] = await Promise.all([
          schoolYearsApi.getAll(),
          schoolYearsApi.getActive().catch(() => ({ data: null })),
        ]);
        const list = Array.isArray(all) ? all : [];
        setAvailableYears(list);
        if (active?.name && !exportYear) setExportYear(active.name);
        else if (list.length && !exportYear) setExportYear(list[0].name || list[0]);
      } catch { /* silent — endpoint may not exist yet */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [studentsRes, classesRes] = await Promise.all([
        studentsApi.getAll({ status: filterStatus }),
        classesApi.getAll()
      ]);
      setStudents(studentsRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedStudent) {
        await studentsApi.update(selectedStudent.id, formData);
        toast.success(t('success'));
      } else {
        await studentsApi.create(formData);
        toast.success(t('success'));
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDelete = async () => {
    try {
      await studentsApi.delete(selectedStudent.id);
      toast.success(t('success'));
      setDeleteDialogOpen(false);
      setSelectedStudent(null);
      loadData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setFormData({
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      father_name: student.father_name || '',
      mother_name: student.mother_name || '',
      parent_contact: student.parent_contact || '',
      email: student.email || '',
      nationality: student.nationality || '',
      address: student.address || '',
      class_id: student.class_id || '',
      cycle: student.cycle || '',
      date_of_birth: student.date_of_birth || '',
      birth_place: student.birth_place || '',
      gender: student.gender || '',
      photo_base64: student.photo_base64 || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedStudent(null);
    setFormData({
      first_name: '',
      last_name: '',
      father_name: '',
      mother_name: '',
      parent_contact: '',
      email: '',
      nationality: '',
      address: '',
      class_id: '',
      cycle: '',
      date_of_birth: '',
      birth_place: '',
      gender: '',
      photo_base64: ''
    });
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const { data } = await studentsApi.import(file);
      const imported = data.imported ?? 0;
      const skipped = data.skipped ?? 0;
      const errors = data.errors || [];
      setStudentsImportResult({ ...data, filename: file.name });
      setStudentsImportOpen(true);
      if (imported === 0 && errors.length > 0) {
        toast.error(
          language === 'fr'
            ? `Import échoué — 0 élève créé, ${errors.length} ligne(s) en erreur`
            : `Import failed — 0 student created, ${errors.length} error(s)`
        );
      } else if (imported > 0 && errors.length === 0) {
        toast.success(
          language === 'fr'
            ? `${imported} élève(s) importé(s) avec succès`
            : `${imported} student(s) imported`
        );
      } else {
        toast.warning(
          language === 'fr'
            ? `${imported} importé(s), ${skipped} ignoré(s), ${errors.length} erreur(s)`
            : `${imported} imported, ${skipped} skipped, ${errors.length} error(s)`
        );
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur d\'import' : 'Import error'));
    }
    e.target.value = '';
  };

  const handleDownloadStudentsTemplate = async () => {
    try {
      const { data } = await studentsApi.importTemplate();
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'modele-eleves.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'Modèle CSV téléchargé' : 'CSV template downloaded');
    } catch (e) {
      toast.error(language === 'fr' ? 'Erreur téléchargement du modèle' : 'Template download failed');
    }
  };

  const handleGenerateCode = async (student) => {
    try {
      const { data } = await studentCodeApi.getCode(student.id);
      setParentCode(data);
      setCodeCopied(false);
      setCodeDialogOpen(true);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(parentCode?.parent_code || '');
    setCodeCopied(true);
    toast.success(t('codeCopied'));
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = filterClass === 'all' || student.class_id === filterClass;
    const matchesCycle = filterCycle === 'all' || student.cycle === filterCycle;
    const isReturning = student.is_new === false;
    const matchesEnrollment = filterEnrollment === 'all'
      || (filterEnrollment === 'returning' ? isReturning : !isReturning);
    return matchesSearch && matchesClass && matchesCycle && matchesEnrollment;
  });

  const cycles = [
    { value: 'maternelle', label: t('maternelle') },
    { value: 'primaire', label: t('primaire') },
    { value: 'college', label: t('college') },
    { value: 'lycee', label: t('lycee') }
  ];

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('students')}</h1>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImport}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={handleDownloadStudentsTemplate}
            className="border-[#E2E8F0]"
            data-testid="download-students-template-btn"
            title={language === 'fr' ? 'Télécharger un modèle CSV prêt à remplir' : 'Download a ready-to-fill CSV template'}
          >
            <DownloadSimple size={18} className="mr-2" />
            {language === 'fr' ? 'Modèle CSV' : 'CSV template'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setImportWizardOpen(true)}
            className="border-[#E2E8F0]"
            data-testid="import-csv-btn"
          >
            <UploadSimple size={18} className="mr-2" />
            {t('importCSV')}
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const { data } = await qrApi.sheetPdf(filterClass !== 'all' ? filterClass : undefined);
                const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                const a = document.createElement('a'); a.href = url; a.download = `qr_codes_${filterClass || 'all'}.pdf`;
                document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
              } catch { toast.error(t('error')); }
            }}
            className="border-[#E2E8F0]"
            data-testid="qr-sheet-btn"
            title="Fiches QR à imprimer"
          >
            <QrCode size={18} className="mr-2" />
            {t('qrSheet')}
          </Button>
          {/* iter88 — Roster export (XLSX + PDF, alphabetical, with birth
              date + place). Uses authenticated blob download so cookies are
              always sent and popup blockers cannot block the download. */}
          <Button
            variant="outline"
            disabled={filterClass === 'all'}
            onClick={async () => {
              try {
                const { data } = await api.get('/students/export/roster.xlsx',
                  { params: { class_id: filterClass }, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([data]));
                const a = document.createElement('a'); a.href = url;
                a.download = `liste_eleves_${filterClass}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
            }}
            className="border-[#E2E8F0]"
            data-testid="roster-xlsx-btn"
            title="Liste alphabétique classe (Excel)"
          >
            📊 Liste Excel
          </Button>
          {/* iter106 — Contacts parents (Excel) + attestations en lot */}
          {['admin', 'director_studies', 'user'].includes(user?.role) && (
            <>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.get('/students/export/contacts.xlsx',
                      { params: { class_id: filterClass }, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([data]));
                    const a = document.createElement('a'); a.href = url;
                    a.download = `contacts_parents_${filterClass}.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success(filterClass === 'all' ? 'Export contacts (toutes classes) téléchargé' : `Export contacts ${filterClass} téléchargé`);
                  } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
                }}
                className="border-[#E2E8F0]"
                data-testid="contacts-xlsx-btn"
                title="Liste élèves + contacts parents (Excel) — toutes les classes si aucun filtre"
              >
                📇 Contacts parents
              </Button>
              <Button
                variant="outline"
                disabled={filterClass === 'all'}
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/classes/${encodeURIComponent(filterClass)}/attestations.pdf`,
                      { params: { type: 'scolarite' }, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                    const a = document.createElement('a'); a.href = url;
                    a.download = `attestations_scolarite_${filterClass}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success(`Attestations de scolarité (${filterClass}) générées`);
                  } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
                }}
                className="border-[#E2E8F0]"
                data-testid="class-attestations-btn"
                title="Attestations de scolarité de toute la classe (un seul PDF)"
              >
                📄 Attestations classe
              </Button>
            </>
          )}
          <Button
            variant="outline"
            disabled={filterClass === 'all'}
            onClick={async () => {
              try {
                const { data } = await api.get('/students/export/roster.pdf',
                  { params: { class_id: filterClass }, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
              } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
            }}
            className="border-[#E2E8F0]"
            data-testid="roster-pdf-btn"
            title="Liste alphabétique classe (PDF)"
          >
            📄 Liste PDF
          </Button>
          <Button
            variant="outline"
            disabled={filterClass === 'all'}
            onClick={async () => {
              try {
                const { data } = await api.get('/students/export/roster.pdf',
                  { params: { class_id: filterClass, include_qr: 1 }, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
              } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
            }}
            className="border-[#E2E8F0]"
            data-testid="roster-qr-btn"
            title="Liste avec QR d'appel individuel imprimable"
          >
            🎯 Liste + QR
          </Button>
          <Button
            variant="outline"
            disabled={filterClass === 'all'}
            onClick={async () => {
              try {
                const { data } = await api.get('/students/export/roster.pdf',
                  { params: { class_id: filterClass, include_photo: 1 }, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
              } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
            }}
            className="border-[#E2E8F0]"
            data-testid="roster-photo-btn"
            title="Trombinoscope avec photos"
          >
            📸 Trombinoscope
          </Button>
          {/* iter91/92 — Toutes classes en une fois avec sélecteur d'année
              scolaire (utile pour archiver les listes d'années précédentes).
              iter92 — Bouton Trombinoscope global (avec photos). */}
          <div className="flex items-center gap-1 border border-emerald-300 rounded-md px-2 bg-emerald-50/50">
            <span className="text-[11px] text-emerald-800 font-medium">
              {language === 'fr' ? 'Année' : 'Year'}:
            </span>
            <select
              value={exportYear}
              onChange={(e) => setExportYear(e.target.value)}
              className="h-8 bg-transparent text-[12px] text-emerald-900 font-semibold outline-none pr-1"
              data-testid="roster-year-select"
              title={language === 'fr'
                ? "Année scolaire à exporter"
                : "School year to export"}
            >
              <option value="">
                {language === 'fr' ? '— Toutes années —' : '— All years —'}
              </option>
              {availableYears.map((y) => (
                <option key={y.id || y.name || y} value={y.name || y}>
                  {y.name || y}{y.is_active ? ' ✓' : ''}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                toast.info(language === 'fr' ? 'Génération de toutes les classes…' : 'Generating all classes…');
                const { data } = await api.get('/students/export/roster-all.xlsx',
                  { params: exportYear ? { school_year: exportYear } : {}, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([data]));
                const a = document.createElement('a'); a.href = url;
                a.download = `liste_eleves_toutes_classes${exportYear ? '_' + exportYear : ''}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
            }}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="roster-all-xlsx-btn"
            title={language === 'fr'
              ? 'Exporter toutes les classes en une seule fois (Excel, 1 feuille par classe)'
              : 'Export all classes at once (Excel, 1 sheet per class)'}
          >
            📚 {language === 'fr' ? 'Toutes classes (Excel)' : 'All classes (Excel)'}
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                toast.info(language === 'fr' ? 'Génération de toutes les classes…' : 'Generating all classes…');
                const { data } = await api.get('/students/export/roster-all.pdf',
                  { params: exportYear ? { school_year: exportYear } : {}, responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
              } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
            }}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="roster-all-pdf-btn"
            title={language === 'fr'
              ? 'Exporter toutes les classes en une seule fois (PDF, section par classe)'
              : 'Export all classes at once (PDF, one section per class)'}
          >
            📚 {language === 'fr' ? 'Toutes classes (PDF)' : 'All classes (PDF)'}
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                toast.info(language === 'fr'
                  ? 'Trombinoscope global en cours (peut prendre 20-40s)…'
                  : 'Building global directory (20-40s)…');
                const params = { include_photo: 1 };
                if (exportYear) params.school_year = exportYear;
                const { data } = await api.get('/students/export/roster-all.pdf',
                  { params, responseType: 'blob', timeout: 120000 });
                const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
              } catch (e) { toast.error(e?.response?.data?.detail || t('error')); }
            }}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="roster-all-trombi-btn"
            title={language === 'fr'
              ? 'Annuaire école : PDF avec photos de tous les élèves, groupés par classe'
              : 'School directory: PDF with all student photos, grouped by class'}
          >
            📸 {language === 'fr' ? 'Trombinoscope global' : 'Global directory'}
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={openBulkServicesDialog}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="bulk-services-btn"
              title={language === 'fr' ? 'Activer Cantine/Transport en masse pour une classe' : 'Bulk enable canteen/transport for a class'}
            >
              <Percent size={18} className="mr-2" />
              {language === 'fr' ? 'Activer services (masse)' : 'Bulk enable services'}
            </Button>
          )}
          {canRegularize && (
            <Button
              variant="outline"
              onClick={openRegularize}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="regularize-returning-btn"
              title={language === 'fr'
                ? 'Marquer en masse les anciens élèves comme réinscrits (fiabilise les soldes)'
                : 'Bulk-mark returning students to fix balances'}
            >
              <ArrowsClockwise size={18} className="mr-2" />
              {language === 'fr' ? 'Régulariser réinscrits' : 'Fix re-registrations'}
            </Button>
          )}
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-[#0052FF] hover:bg-[#0043D1]"
            data-testid="add-student-btn"
          >
            <Plus size={18} className="mr-2" />
            {t('addStudent')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
                <Input
                  placeholder={t('search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-[#E2E8F0]"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={filterCycle} onValueChange={setFilterCycle}>
              <SelectTrigger className="w-[180px] border-[#E2E8F0]" data-testid="filter-cycle">
                <SelectValue placeholder={t('cycle')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {cycles.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterClass} onValueChange={setFilterClass}>
              <SelectTrigger className="w-[180px] border-[#E2E8F0]" data-testid="filter-class">
                <SelectValue placeholder={t('class')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {classes.map(c => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px] border-[#E2E8F0]" data-testid="filter-status">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="archived">📦 Archivés</SelectItem>
                <SelectItem value="graduated">🎓 Diplômés</SelectItem>
                <SelectItem value="all">Tous</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterEnrollment} onValueChange={setFilterEnrollment}>
              <SelectTrigger className="w-[170px] border-[#E2E8F0]" data-testid="filter-enrollment">
                <SelectValue placeholder="Inscription" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="new">🆕 Nouveaux</SelectItem>
                <SelectItem value="returning">🔁 Réinscrits</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      {/* Enrollment status counters (active list) */}
      <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="enrollment-counters">
        <span className="text-[#64748B] font-manrope">Sur {students.length} élève(s) affiché(s) :</span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium" data-testid="counter-new">
          🆕 Nouveaux : <b>{students.filter(s => s.is_new !== false).length}</b>
        </span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium" data-testid="counter-returning">
          🔁 Réinscrits : <b>{students.filter(s => s.is_new === false).length}</b>
        </span>
      </div>
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('lastName')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('firstName')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('class')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('cycle')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">Statut</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('parentContact')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF]" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#64748B]">
                    {t('noData')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredStudents.map((student) => (
                  <TableRow key={student.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]" data-testid={`student-row-${student.id}`}>
                    <TableCell className="font-manrope">
                      <div className="flex items-center gap-2">
                        {student.photo_base64 ? (
                          <img src={student.photo_base64} alt=""
                            className="w-8 h-8 rounded-full object-cover border border-[#E2E8F0]" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#F1F5F9] border border-[#E2E8F0] flex items-center justify-center text-xs font-bold text-[#94A3B8]">
                            {student.gender === 'F' ? '♀' : student.gender === 'M' ? '♂' : '·'}
                          </div>
                        )}
                        <div>
                          <div>{student.last_name}</div>
                          {student.gender && (
                            <div className="text-[10px] text-[#94A3B8]">
                              {student.gender === 'F' ? 'Féminin' : student.gender === 'M' ? 'Masculin' : student.gender}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-manrope">{student.first_name}</TableCell>
                    <TableCell className="font-manrope">{student.class_id}</TableCell>
                    <TableCell className="font-manrope">{t(student.cycle) || student.cycle}</TableCell>
                    <TableCell className="font-manrope">
                      {student.is_new === false ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`enrollment-badge-${student.id}`}>
                          🔁 Réinscrit
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200" data-testid={`enrollment-badge-${student.id}`}>
                          🆕 Nouveau
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-manrope">{student.parent_contact}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleGenerateCode(student)}
                        className="hover:bg-[#10B981]/10 text-[#10B981]"
                        title={t('generateCode')}
                        data-testid={`code-student-${student.id}`}
                      >
                        <Key size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/students/${student.id}/cursus`)}
                        className="hover:bg-[#0052FF]/10 text-[#0052FF]"
                        title={t('cursus')}
                        data-testid={`cursus-student-${student.id}`}
                      >
                        <GraduationCap size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(student)}
                        className="hover:bg-[#EFF6FF] text-[#0052FF]"
                        data-testid={`edit-student-${student.id}`}
                      >
                        <PencilSimple size={18} />
                      </Button>
                      <AttestationMenu student={student} />
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDiscountDialog(student)}
                          className="hover:bg-[#FEF3C7] text-amber-600"
                          title={language === 'en' ? 'Discounts' : 'Remises'}
                          data-testid={`discount-btn-${student.id}`}
                        >
                          <Percent size={18} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedStudent(student); setDeleteDialogOpen(true); }}
                        className="hover:bg-[#FEF2F2] text-[#EF4444]"
                        data-testid={`delete-student-${student.id}`}
                      >
                        <Trash size={18} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <StudentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedStudent={selectedStudent}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        classes={classes}
        cycles={cycles}
        t={t}
        onToggleEnrollment={canRegularize ? handleToggleEnrollment : null}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-outfit">{t('confirm')}</AlertDialogTitle>
            <AlertDialogDescription className="font-manrope">
              {t('confirmDelete')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#EF4444] hover:bg-red-600">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regularize returning students */}
      <AlertDialog open={regularizeOpen} onOpenChange={setRegularizeOpen}>
        <AlertDialogContent data-testid="regularize-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-outfit">
              {language === 'fr' ? 'Régulariser les réinscriptions' : 'Fix re-registrations'}
            </AlertDialogTitle>
            <AlertDialogDescription className="font-manrope">
              {language === 'fr'
                ? 'Marque comme « réinscrits » tous les élèves ayant un historique (paiement d\'une année antérieure ou réinscription cette année). Les frais d\'inscription ne seront alors plus facturés à ces élèves.'
                : 'Marks as "returning" all students with history. Registration fees will no longer be charged to them.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {regularizePreview ? (
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3 space-y-1.5 text-sm font-manrope" data-testid="regularize-preview">
              <div className="flex justify-between"><span className="text-[#64748B]">{language === 'fr' ? 'Année active' : 'Active year'}</span><span className="font-semibold">{regularizePreview.active_year}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">{language === 'fr' ? 'Élèves avec historique' : 'Students with history'}</span><span className="font-semibold">{regularizePreview.candidates}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">{language === 'fr' ? 'Déjà réinscrits' : 'Already returning'}</span><span>{regularizePreview.already_returning}</span></div>
              <div className="flex justify-between border-t border-emerald-200/60 pt-1.5"><span className="text-emerald-800 font-semibold">{language === 'fr' ? 'À régulariser' : 'To update'}</span><span className="text-emerald-800 font-bold" data-testid="regularize-to-update">{regularizePreview.to_update}</span></div>
            </div>
          ) : (
            <p className="text-sm text-[#64748B] font-manrope">{language === 'fr' ? 'Calcul en cours…' : 'Calculating…'}</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="regularize-cancel">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmRegularize(); }}
              disabled={regularizing || !regularizePreview || regularizePreview.to_update === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="regularize-confirm"
            >
              {regularizing
                ? (language === 'fr' ? 'Traitement…' : 'Processing…')
                : (language === 'fr' ? `Régulariser (${regularizePreview?.to_update ?? 0})` : `Fix (${regularizePreview?.to_update ?? 0})`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Parent Code Dialog */}
      <ParentCodeDialog
        open={codeDialogOpen}
        onOpenChange={setCodeDialogOpen}
        parentCode={parentCode}
        codeCopied={codeCopied}
        onCopy={handleCopyCode}
        language={language}
        t={t}
      />

      {/* Admin Discounts Dialog */}
      <StudentDiscountDialog
        open={discountDialogOpen}
        onOpenChange={setDiscountDialogOpen}
        target={discountTarget}
        form={discountForm}
        setForm={setDiscountForm}
        onSave={saveDiscounts}
        saving={discountSaving}
        language={language}
        t={t}
      />

      {/* Bulk activate services dialog (admin only) */}
      <BulkServicesDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        form={bulkForm}
        setForm={setBulkForm}
        classes={classes}
        transportZones={transportZonesList}
        onSubmit={submitBulkServices}
        saving={bulkSaving}
        bulkResult={bulkResult}
        csvDownloading={csvDownloading}
        onDownloadCsvTemplate={handleDownloadCsvTemplate}
        csvUploading={csvUploading}
        onCsvUpload={handleCsvUpload}
        csvResult={csvResult}
        language={language}
      />

      {/* iter52 — Students Import Wizard (csv + xlsx + column mapping) */}
      <ImportWizard
        open={importWizardOpen}
        onOpenChange={setImportWizardOpen}
        title={language === 'fr' ? 'Importer des élèves' : 'Import students'}
        kind="students"
        api={{
          importPreview: (file) => studentsApi.import(file, { previewOnly: true }),
          importFinal: (file, opts) => studentsApi.import(file, opts),
        }}
        onImported={(data) => {
          setStudentsImportResult({ ...data, filename: data.filename });
          if (!data.dry_run && (data.imported ?? 0) > 0) loadData();
        }}
      />

      {/* iter51 — Students CSV import report */}
      <StudentsImportReportDialog
        open={studentsImportOpen}
        onOpenChange={setStudentsImportOpen}
        result={studentsImportResult}
        language={language}
      />

    </div>
  );
};

export default Students;
